// Where everyone lives, and where the candidates are.
//
// /regions ranks by the worst-off household's door-to-door travel. The ranking is correct
// but abstract: "11 h" says a region is bad for somebody without saying who, or where.
// This is the other half of that answer — six household pins and a candidate anchor make
// "this is brutal for the Uhrichs" self-evident instead of something an organizer asserts.
//
// The map measures NOTHING. Every hour and dollar on this screen still comes from Distance
// Matrix via the burden engine. Straight lines here are sightlines, not routes.
//
// Privacy: households are plotted at ZIP centroid, never a street address, and the zoom is
// capped so the pin never implies precision the data doesn't have.

import { useEffect, useRef, useState, type JSX } from 'react'
import { isMapsConfigured, loadGoogleMaps } from '../lib/googleMaps'

export interface MapHousehold {
  id: string
  label: string
  lat: number
  lng: number
}

export interface MapRegion {
  region_id: string
  name: string
  lat: number
  lng: number
}

export interface RegionMapProps {
  households: MapHousehold[]
  regions: MapRegion[]
  /** The candidate currently in focus; the viewport fits it plus every household. */
  selectedRegionId: string | null
  /** Household that sets the selected region's score. Highlighted to match the ▲ chip. */
  worstHouseholdId: string | null
  onSelectRegion?: (regionId: string) => void
}

/** ZIP-centroid data does not justify street-level zoom. */
const MAX_ZOOM = 11

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type G = any

export function RegionMap({
  households,
  regions,
  selectedRegionId,
  worstHouseholdId,
  onSelectRegion,
}: RegionMapProps): JSX.Element | null {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<G>(null)
  const markersRef = useRef<G[]>([])
  const [status, setStatus] = useState<'idle' | 'ready' | 'failed'>('idle')

  // Load the script once the component is actually on screen.
  useEffect(() => {
    if (!isMapsConfigured) return
    let cancelled = false
    loadGoogleMaps()
      .then(() => {
        if (!cancelled) setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('failed')
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Draw. Re-runs when the data or the selection changes.
  useEffect(() => {
    if (status !== 'ready' || !hostRef.current) return
    const g = (globalThis as { google?: G }).google
    // `google.maps` existing is not the same as it being ready — under `loading=async` the
    // enums land before the constructors. Check what we actually construct from.
    if (typeof g?.maps?.Map !== 'function' || typeof g?.maps?.Marker !== 'function') return

    if (!mapRef.current) {
      mapRef.current = new g.maps.Map(hostRef.current, {
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        maxZoom: MAX_ZOOM,
      })
    }
    const map = mapRef.current

    for (const m of markersRef.current) m.setMap(null)
    markersRef.current = []

    const bounds = new g.maps.LatLngBounds()

    for (const h of households) {
      const isWorst = h.id === worstHouseholdId
      const marker = new g.maps.Marker({
        map,
        position: { lat: h.lat, lng: h.lng },
        title: isWorst ? `${h.label} — worst-off for this region` : h.label,
        label: { text: h.label.slice(0, 1), color: '#fff', fontSize: '11px' },
        zIndex: isWorst ? 3 : 1,
        icon: {
          path: g.maps.SymbolPath.CIRCLE,
          scale: isWorst ? 11 : 8,
          fillColor: isWorst ? '#b34e17' : '#0f6b5c',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: isWorst ? 3 : 2,
        },
      })
      markersRef.current.push(marker)
      bounds.extend(marker.getPosition())
    }

    for (const r of regions) {
      const selected = r.region_id === selectedRegionId
      const marker = new g.maps.Marker({
        map,
        position: { lat: r.lat, lng: r.lng },
        title: r.name,
        zIndex: selected ? 4 : 2,
        opacity: selectedRegionId && !selected ? 0.45 : 1,
        icon: {
          path: 'M 0,0 L -7,-16 L 7,-16 Z', // downward pin
          scale: selected ? 1.5 : 1,
          fillColor: selected ? '#b34e17' : '#3c4a47',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 1.5,
        },
      })
      if (onSelectRegion) marker.addListener('click', () => onSelectRegion(r.region_id))
      markersRef.current.push(marker)
      // Only the focused candidate frames the view; all of them would zoom to the whole
      // eastern seaboard and tell you nothing.
      if (selected || !selectedRegionId) bounds.extend(marker.getPosition())
    }

    if (!bounds.isEmpty()) map.fitBounds(bounds, 48)
  }, [status, households, regions, selectedRegionId, worstHouseholdId, onSelectRegion])

  if (!isMapsConfigured) {
    return (
      <div className="spz-map-fallback">
        <p className="spz-sub">
          Map unavailable &mdash; this build has no <code>VITE_GOOGLE_MAPS_BROWSER_KEY</code>.
          Rankings below are unaffected.
        </p>
      </div>
    )
  }
  if (status === 'failed') {
    return (
      <div className="spz-map-fallback">
        <p className="spz-sub">
          Map couldn&rsquo;t load. The rankings below are unaffected.
        </p>
      </div>
    )
  }
  if (households.length === 0 && regions.length === 0) return null

  return (
    <div className="spz-map-wrap">
      <div ref={hostRef} className="spz-map" role="img" aria-label="Map of households and candidate regions" />
      <p className="spz-sub spz-map-note">
        Households are shown at ZIP-code level, not street addresses. Distances come from the
        travel calculations, not from this map.
      </p>
    </div>
  )
}

export default RegionMap
